import { useEffect, useState } from 'react';
import { taskAPI } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { StellarSelect, StellarDatePicker } from './StellarFormControls';
import { AssigneeMultiSelect } from './AssigneeMultiSelect';
import { ErrorBoundary } from './ErrorBoundary';
import { AutoGrowTextarea } from './AutoGrowTextarea';
import { AttachmentsField } from './AttachmentsField';
import { ChevronDown, ChevronRight, Lock, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Batch 3 B8 — Unified Task Creation Modal.
 *
 * Single source of truth for "+ New Task" across the app. Used by:
 *   - UnifiedTasksPage (general task create)
 *   - MyClientsPage   (client-scoped task create — pass `prefilledClientId`)
 *
 * Includes Batch 3 A3 `deliverable_type` field, surfaced only when a client
 * is selected and that client has deliverables (Posts/Stories/Reels or any
 * custom_deliverable_types). Hidden otherwise.
 */
export function TaskCreateModal({
  open,
  onOpenChange,
  workspaceId,
  members = [],
  clients = [],
  prefilledClientId = '',
  currentUserId = '',
  defaultDueDate = '',  // Batch 5 R2 B-Block CAL-007 — calendar empty-day pre-fill.
  activatePrefill = null,  // Idea Bank activation — see IdeaBankPage.
  onCreated,
  testIdPrefix = 'new-task',
}) {
  const initialState = () => ({
    title: '', description: '',
    assigned_to: currentUserId ? [currentUserId] : [],
    accountable_to: [],
    client_group_id: prefilledClientId || '',
    due_date: defaultDueDate || '', priority: 'medium', status: 'not_started',
    labels: [], estimated_hours: 0,
    follow_ups: [],
    external_links: [{ name: '', url: '' }],
    internal_links: [{ name: '', path: '' }],
    drive_link: '',
    deliverable_type: '',
    attachments: [],
  });
  const [task, setTask] = useState(initialState);
  const [priorityManuallySet, setPriorityManuallySet] = useState(false);
  const [moreOptions, setMoreOptions] = useState(() => localStorage.getItem('stellar_task_more_options') === 'true');
  const [submitting, setSubmitting] = useState(false);
  // D52b Part D — Task Templates dropdown.
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Fetch available templates whenever the modal opens.
  useEffect(() => {
    if (!open || !workspaceId) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/task-templates/${workspaceId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
    })
      .then((r) => r.ok ? r.json() : { templates: [] })
      .then((d) => setTemplates(d.templates || []))
      .catch(() => setTemplates([]));
  }, [open, workspaceId]);

  // Apply the selected template — fetches a server-rendered preview with
  // {client}/{month}/{year} placeholders resolved against the currently
  // chosen client.
  const applyTemplate = async (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const q = task.client_group_id ? `?client_group_id=${encodeURIComponent(task.client_group_id)}` : '';
      const r = await fetch(`${API}/api/task-templates/${templateId}/preview${q}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      const rendered = data.rendered || {};
      setTask((p) => ({
        ...p,
        title: rendered.title || p.title,
        description: rendered.description || p.description,
        priority: rendered.priority || p.priority,
        assigned_to: (rendered.assigned_to && rendered.assigned_to.length) ? rendered.assigned_to : p.assigned_to,
        estimated_hours: rendered.estimated_hours || p.estimated_hours,
        due_date: rendered.due_date || p.due_date,
        deliverable_type: rendered.deliverable_type || p.deliverable_type,
        labels: (rendered.labels && rendered.labels.length) ? rendered.labels : p.labels,
      }));
      if (rendered.priority && rendered.priority !== 'medium') setPriorityManuallySet(true);
      toast.success('Template applied');
    } catch (e) {
      toast.error(`Could not apply template: ${e.message}`);
    }
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setTask(initialState());
      setPriorityManuallySet(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Re-apply prefilled client when modal opens or prop changes
  useEffect(() => {
    if (open) {
      setTask(p => ({
        ...p,
        client_group_id: prefilledClientId || p.client_group_id,
        // Batch 5 R2 B-Block CAL-007 — re-apply due_date when caller supplies one.
        due_date: defaultDueDate || p.due_date,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefilledClientId, defaultDueDate]);

  // Idea Bank Activate flow — when the parent supplies an `activatePrefill`
  // payload, mirror those values into the task draft so the user sees the
  // idea's title/description/assignees/priority/attachments pre-filled.
  // The user still has to pick a due date manually (hint shown below).
  useEffect(() => {
    if (!open || !activatePrefill) return;
    setTask((p) => ({
      ...p,
      title: activatePrefill.title || p.title,
      description: activatePrefill.description || p.description,
      assigned_to: (activatePrefill.assigned_to && activatePrefill.assigned_to.length)
        ? activatePrefill.assigned_to
        : p.assigned_to,
      priority: activatePrefill.priority || p.priority,
      attachments: (activatePrefill.attachments && activatePrefill.attachments.length)
        ? activatePrefill.attachments
        : p.attachments,
    }));
    if (activatePrefill.priority && activatePrefill.priority !== 'medium') {
      setPriorityManuallySet(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activatePrefill?.title, activatePrefill?.description]);

  // Auto-suggest priority from due_date (unless user explicitly set one)
  useEffect(() => {
    if (priorityManuallySet) return;
    if (!task.due_date) return;
    try {
      const due = new Date(task.due_date + 'T00:00:00');
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const days = Math.floor((due - today) / 86400000);
      const suggested = (days < 0 || days <= 5) ? 'high' : (days <= 10 ? 'medium' : 'low');
      if (suggested !== task.priority) setTask(p => ({ ...p, priority: suggested }));
    } catch {}
  }, [task.due_date, priorityManuallySet]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute allowed deliverable types from selected client (A3).
  // Iter 71.1 — spec change: ALWAYS show the 5 baseline options
  // (Post / Story / Reel / None / Other) when a client is selected,
  // regardless of the client's posts/stories/reels counts. Concat any
  // custom types defined on the client. Reserved-name enforcement on
  // custom-type ADD is unchanged (server-side, separate code path).
  //
  // ⚠️ Radix Select forbids SelectItem value="". We map "None" to the
  // `__none__` sentinel for the dropdown, and translate back to "" on
  // both reads (display) and writes (save), preserving the on-the-wire
  // contract (`task.deliverable_type === ''` ↔ "None").
  const selectedClient = (clients || []).find(c => c && c.id === task.client_group_id) || null;
  const allowedDeliverableTypes = (() => {
    if (!selectedClient) return [];
    const out = [
      { value: 'Post', label: 'Post' },
      { value: 'Story', label: 'Story' },
      { value: 'Reel', label: 'Reel' },
      { value: '__none__', label: 'None' },
      { value: 'Other', label: 'Other' },
    ];
    const reserved = new Set(['Post', 'Story', 'Reel', 'None', 'Other', '__none__']);
    const custom = Array.isArray(selectedClient.custom_deliverable_types)
      ? selectedClient.custom_deliverable_types
      : [];
    for (const ct of custom) {
      const name = (ct && typeof ct.type_name === 'string' ? ct.type_name : '').trim();
      if (name && !reserved.has(name)) {
        out.push({ value: name, label: name });
        reserved.add(name);
      }
    }
    return out;
  })();
  // Reset deliverable_type if client changes and current value is no longer valid
  useEffect(() => {
    if (!selectedClient) {
      if (task.deliverable_type) setTask(p => ({ ...p, deliverable_type: '' }));
      return;
    }
    const validValues = new Set(allowedDeliverableTypes.map(o => o.value));
    if (task.deliverable_type && !validValues.has(task.deliverable_type)) {
      setTask(p => ({ ...p, deliverable_type: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.client_group_id, selectedClient?.deliverables, selectedClient?.custom_deliverable_types]);

  const submit = async () => {
    if (!task.title.trim()) { toast.error('Title required'); return; }
    if (activatePrefill && !task.due_date) {
      toast.error('Set a due date to activate this idea as a live task');
      return;
    }
    const validFollowUps = (task.follow_ups || []).filter(f => f.date);
    if (validFollowUps.length && task.due_date) {
      const bad = validFollowUps.find(f => f.date >= task.due_date);
      if (bad) { toast.error(`Follow-up "${bad.label || bad.date}" must be before the due date`); return; }
    }
    if (validFollowUps.length && !task.due_date) {
      toast.error('Set a due date before adding follow-ups'); return;
    }
    setSubmitting(true);
    try {
      const res = await taskAPI.create({
        workspace_id: workspaceId,
        title: task.title.trim(),
        description: task.description || '',
        assigned_to: task.assigned_to.length ? task.assigned_to : (currentUserId ? [currentUserId] : []),
        accountable_to: task.accountable_to || [],
        // Batch 2 MINOR — backend now rejects empty-string client_group_id
        // with 400. Send null (legitimate unassigned) instead.
        client_group_id: task.client_group_id || null,
        due_date: task.due_date || null,
        priority: task.priority,
        status: task.status,
        labels: task.labels,
        estimated_hours: task.estimated_hours || 0,
        follow_ups: validFollowUps.map(f => ({ date: f.date, label: f.label || '', completed: false })),
        recurrence: task.recurrence || '',
        external_links: (task.external_links || []).filter(l => l.name || l.url),
        internal_links: (task.internal_links || []).filter(l => l.name || l.path),
        drive_link: task.drive_link || '',
        deliverable_type: task.deliverable_type || null,
        priority_locked: priorityManuallySet,
        attachments: task.attachments || [],
      });
      toast.success('Task created');
      onOpenChange(false);
      if (onCreated) onCreated(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : 'Failed to create task'));
    }
    setSubmitting(false);
  };

  const headerTitle = activatePrefill
    ? 'Activate Idea → New Task'
    : (prefilledClientId && selectedClient) ? `New Task for ${selectedClient.name}` : 'New Task';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[85vh] overflow-y-auto bg-card border-border/40" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-[17px]">{headerTitle}</DialogTitle>
          {/* BUG-T-007 / BUG-T-015 (2026-05-21) — explicit DialogDescription
              silences Radix's missing-`aria-describedby` a11y warning.
              Visually subtle; screen readers announce the context. */}
          <DialogDescription className="text-[12px] text-muted-foreground/60">
            {activatePrefill
              ? 'Set a due date to convert this idea into a live task. Other fields are pre-filled from the Idea Bank.'
              : prefilledClientId && selectedClient
              ? `Create a task scoped to ${selectedClient.name}. Title is required.`
              : 'Title is required. All other fields are optional.'}
          </DialogDescription>
        </DialogHeader>
        <ErrorBoundary label="the New Task form">
        <div className="space-y-4 py-2">
          {/* D52b Part D — "Start from template" dropdown. Hidden when no
              templates configured yet. Applying a template re-fetches a
              server-rendered preview that resolves {client} placeholders
              against the currently selected client. */}
          {templates.length > 0 && (
            <div className="space-y-1.5 rounded-md border border-gold/20 bg-gold/[0.04] p-2.5" data-testid={`${testIdPrefix}-template-picker-wrap`}>
              <Label className="text-[10px] uppercase tracking-wider text-gold/80">Start from template (optional)</Label>
              <select
                value={selectedTemplateId}
                onChange={(e) => applyTemplate(e.target.value)}
                className="w-full h-9 text-[12px] rounded-md border border-input bg-background px-2"
                data-testid={`${testIdPrefix}-template-picker`}
              >
                <option value="">— Don't use a template —</option>
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground/60">
                Pick a client first to resolve {'{client}'} placeholders. Templates are managed in Settings → Task Templates.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-[12px] text-foreground/70">Title *</Label>
            <Input value={task.title} onChange={e => setTask(p => ({ ...p, title: e.target.value }))} placeholder="What needs to be done?" className="h-10 text-[14px] bg-foreground/[0.02] border-border/30" autoFocus data-testid={`${testIdPrefix}-title`} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[12px] text-foreground/70">Description</Label>
            <AutoGrowTextarea value={task.description} onChange={e => setTask(p => ({ ...p, description: e.target.value }))} placeholder="Optional details..." minRows={6} className="text-[13px]" data-testid={`${testIdPrefix}-description`} />
          </div>

          {/* 2026-06-16 — Attachments. Visible by default (NOT under
              "Less options"). Uses the universal AttachmentsField which
              uploads to /api/media/upload immediately so the metadata is
              ready when the task is created. */}
          <AttachmentsField
            value={task.attachments}
            onChange={(next) => setTask((p) => ({ ...p, attachments: next }))}
            workspaceId={workspaceId}
            testIdPrefix={`${testIdPrefix}-attachments`}
          />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-[12px] text-foreground/70">Assignees</Label>
                {currentUserId && (
                  <label
                    className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground cursor-pointer select-none"
                    data-testid={`${testIdPrefix}-assign-to-me-label`}
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-gold cursor-pointer"
                      checked={(task.assigned_to || []).includes(currentUserId)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setTask((p) => {
                          const cur = p.assigned_to || [];
                          if (checked && !cur.includes(currentUserId)) {
                            return { ...p, assigned_to: [...cur, currentUserId] };
                          }
                          if (!checked) {
                            return { ...p, assigned_to: cur.filter((x) => x !== currentUserId) };
                          }
                          return p;
                        });
                      }}
                      data-testid={`${testIdPrefix}-assign-to-me`}
                    />
                    Assign to me
                  </label>
                )}
              </div>
              <AssigneeMultiSelect members={members} selected={task.assigned_to} onChange={(ids) => setTask(p => ({ ...p, assigned_to: ids }))} showRole={true} testIdPrefix={`${testIdPrefix}-assignee`} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] text-foreground/70">Client</Label>
              <StellarSelect value={task.client_group_id || '__none__'} onValueChange={(v) => setTask(p => ({ ...p, client_group_id: v === '__none__' ? '' : v }))} options={[{ value: '__none__', label: 'No client' }, ...clients.map(c => ({ value: c.id, label: c.name }))]} testId={`${testIdPrefix}-client`} />
              {/* Batch 1 ITEM 1 (BUG-RW-GT-008) — amber soft-warning so users
                  don't accidentally create orphaned tasks that won't show up
                  on any client board. */}
              {!task.client_group_id && (
                <p className="text-[10.5px] text-amber-300/80 leading-snug flex items-start gap-1" data-testid={`${testIdPrefix}-no-client-warning`}>
                  <AlertTriangle className="h-3 w-3 text-amber-300 mt-0.5 shrink-0" />
                  Tasks without a client will not appear on any client board.
                </p>
              )}
            </div>
          </div>
          {/* 2026-07 — Accountable to: optional multi-user watchers who get
              notified on status changes, submissions, and comments. */}
          <div className="space-y-1.5">
            <Label className="text-[12px] text-foreground/70">
              Accountable to <span className="text-muted-foreground/50 font-normal">(optional — they get notified of updates)</span>
            </Label>
            <AssigneeMultiSelect members={members} selected={task.accountable_to || []} onChange={(ids) => setTask(p => ({ ...p, accountable_to: ids }))} showRole={true} placeholder="No one" testIdPrefix={`${testIdPrefix}-accountable`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[12px] text-foreground/70 flex items-center gap-1.5">
                Priority
                {!priorityManuallySet && task.due_date && <span className="text-[9px] text-gold/60">auto-suggested</span>}
                {priorityManuallySet && <Lock className="h-2.5 w-2.5 text-muted-foreground/40" />}
              </Label>
              <div className="flex gap-1.5">
                {[{ v: 'high', l: 'High', sel: 'border-red-500 text-red-400 bg-red-500/15', tent: 'border-red-500/40 text-red-400 bg-transparent', dot: 'bg-red-500' }, { v: 'medium', l: 'Med', sel: 'border-gold text-gold bg-gold/15', tent: 'border-gold/40 text-gold bg-transparent', dot: 'bg-gold' }, { v: 'low', l: 'Low', sel: 'border-foreground/30 text-muted-foreground/60 bg-foreground/[0.05]', tent: 'border-foreground/20 text-muted-foreground/50 bg-transparent', dot: 'bg-foreground/30' }].map(p => {
                  const isExplicit = priorityManuallySet && task.priority === p.v;
                  const isTentative = !priorityManuallySet && task.due_date && task.priority === p.v;
                  const cls = isExplicit ? p.sel : isTentative ? p.tent : 'border-border/20 text-muted-foreground/25';
                  return (
                    <button key={p.v} onClick={() => {
                      if (priorityManuallySet && task.priority === p.v) setPriorityManuallySet(false);
                      else { setPriorityManuallySet(true); setTask(prev => ({ ...prev, priority: p.v })); }
                    }} className={`flex-1 py-2 rounded-lg text-[12px] font-semibold border-2 transition-all flex items-center justify-center gap-1.5 ${cls}`}
                      data-testid={`${testIdPrefix}-priority-${p.v}`} aria-pressed={isExplicit}>
                      <span className={`h-2 w-2 rounded-full ${(isExplicit || isTentative) ? p.dot : 'bg-foreground/10'}`} />{p.l}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Batch 3 A3 — Deliverable Type (hidden when no client).
                Iter 71.1: visibility gate is `selectedClient` only — the 5
                baseline options are always present so the dropdown is
                guaranteed to have entries the moment a client is picked.
                Empty-string `deliverable_type` (= None) is mapped to the
                `__none__` sentinel for the Radix Select (which forbids "").*/}
            {selectedClient && (
              <div className="space-y-1.5" data-testid={`${testIdPrefix}-deliverable-type-wrap`}>
                <Label className="text-[12px] text-foreground/70">Deliverable Type</Label>
                <StellarSelect
                  value={task.deliverable_type === '' ? '__none__' : (task.deliverable_type || '__none__')}
                  onValueChange={(v) => setTask(p => ({ ...p, deliverable_type: v === '__none__' ? '' : v }))}
                  options={allowedDeliverableTypes}
                  testId={`${testIdPrefix}-deliverable-type`}
                />
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-[12px] text-foreground/70 flex items-center gap-1.5">
              Due Date
              {activatePrefill && !task.due_date && (
                <span className="text-[10px] text-gold/80 font-medium">required to activate</span>
              )}
            </Label>
            <StellarDatePicker value={task.due_date} onChange={(d) => setTask(p => ({ ...p, due_date: d }))} placeholder="Select due date" testId={`${testIdPrefix}-due-date`} />
          </div>

          <button onClick={() => { const next = !moreOptions; setMoreOptions(next); localStorage.setItem('stellar_task_more_options', String(next)); }} className="flex items-center gap-1.5 text-[12px] text-gold hover:underline font-medium" data-testid={`${testIdPrefix}-more-options-toggle`}>
            {moreOptions ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} {moreOptions ? 'Less options' : 'More options'}
          </button>
          {moreOptions && (
            <div className="space-y-3 pl-3 border-l-2 border-gold/15">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[11px] text-foreground/60">Status</Label>
                  <StellarSelect value={task.status} onValueChange={(v) => setTask(p => ({ ...p, status: v }))} options={[{ value: 'not_started', label: 'Not Started' }, { value: 'in_progress', label: 'In Progress' }, { value: 'review', label: 'Review' }]} testId={`${testIdPrefix}-status`} />
                </div>
              </div>
              <div className="space-y-1"><Label className="text-[11px] text-foreground/60">Labels (comma separated)</Label><Input value={task.labels.join(', ')} onChange={e => setTask(p => ({ ...p, labels: e.target.value.split(',').map(l => l.trim()).filter(Boolean) }))} placeholder="design, urgent, Q2" className="h-9 text-[12px] bg-card border-border/30" /></div>
              <div className="space-y-1">
                <Label className="text-[11px] text-foreground/60">Recurrence</Label>
                <StellarSelect value={task.recurrence || 'once'} onValueChange={(v) => setTask(p => ({ ...p, recurrence: v }))} options={[{ value: 'once', label: 'Once (no repeat)' }, { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' }]} testId={`${testIdPrefix}-recurrence`} />
              </div>
              <div className="space-y-1.5" data-testid={`${testIdPrefix}-followups-section`}>
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] text-foreground/60">Follow-ups (max 3)</Label>
                  {(task.follow_ups || []).length < 3 && (
                    <button onClick={() => setTask(p => ({ ...p, follow_ups: [...(p.follow_ups || []), { date: '', label: `Follow-up ${(p.follow_ups || []).length + 1}`, completed: false }] }))} className="text-[10px] text-gold hover:underline font-medium" data-testid={`${testIdPrefix}-add-followup-btn`}>+ Add follow-up</button>
                  )}
                </div>
                {(task.follow_ups || []).length === 0 && <p className="text-[10px] text-muted-foreground/30 italic">No follow-ups yet — add up to 3 checkpoints before the due date.</p>}
                {(task.follow_ups || []).map((fu, i) => {
                  const dateAfterDue = fu.date && task.due_date && fu.date >= task.due_date;
                  return (
                    <div key={i} className="grid grid-cols-[110px_1fr_28px] gap-2 items-end" data-testid={`${testIdPrefix}-followup-row-${i}`}>
                      <div>
                        <span className="text-[9px] text-foreground/40 block mb-1">Date</span>
                        <StellarDatePicker value={fu.date || ''} onChange={(d) => { const fs = [...(task.follow_ups || [])]; fs[i] = { ...fs[i], date: d }; setTask(p => ({ ...p, follow_ups: fs })); }} placeholder="Pick" size="sm" maxDate={task.due_date || undefined} clearable={false} testId={`${testIdPrefix}-followup-date-${i}`} />
                        {dateAfterDue && <span className="text-[9px] text-red-400 block mt-0.5">Must be before due date</span>}
                      </div>
                      <div>
                        <span className="text-[9px] text-foreground/40 block mb-1">Label</span>
                        <Input value={fu.label || ''} onChange={e => { const fs = [...(task.follow_ups || [])]; fs[i] = { ...fs[i], label: e.target.value }; setTask(p => ({ ...p, follow_ups: fs })); }} className="h-8 text-[11px] bg-card border-border/30" placeholder="e.g. Draft, Review" />
                      </div>
                      <button onClick={() => setTask(p => ({ ...p, follow_ups: (p.follow_ups || []).filter((_, j) => j !== i) }))} className="h-8 w-7 flex items-center justify-center text-muted-foreground/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" aria-label="Remove follow-up" data-testid={`${testIdPrefix}-remove-followup-${i}`}><Trash2 className="h-3 w-3" /></button>
                    </div>
                  );
                })}
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] text-foreground/60">External Links</Label>
                {(task.external_links || [{ name: '', url: '' }]).map((link, i) => (
                  <div key={i} className="grid grid-cols-2 gap-2">
                    <Input value={link.name} onChange={e => { const links = [...(task.external_links || [{ name: '', url: '' }])]; links[i].name = e.target.value; setTask(p => ({ ...p, external_links: links })); }} placeholder="Link name" className="h-8 text-[11px] bg-card border-border/30" />
                    <Input value={link.url} onChange={e => { const links = [...(task.external_links || [{ name: '', url: '' }])]; links[i].url = e.target.value; setTask(p => ({ ...p, external_links: links })); }} placeholder="https://..." className="h-8 text-[11px] bg-card border-border/30" />
                  </div>
                ))}
                <button onClick={() => setTask(p => ({ ...p, external_links: [...(p.external_links || []), { name: '', url: '' }] }))} className="text-[10px] text-gold hover:underline">+ Add link</button>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] text-foreground/60">Internal Links</Label>
                {(task.internal_links || [{ name: '', path: '' }]).map((link, i) => (
                  <div key={i} className="grid grid-cols-2 gap-2">
                    <Input value={link.name} onChange={e => { const links = [...(task.internal_links || [{ name: '', path: '' }])]; links[i].name = e.target.value; setTask(p => ({ ...p, internal_links: links })); }} placeholder="Link name" className="h-8 text-[11px] bg-card border-border/30" />
                    <Input value={link.path} onChange={e => { const links = [...(task.internal_links || [{ name: '', path: '' }])]; links[i].path = e.target.value; setTask(p => ({ ...p, internal_links: links })); }} placeholder="/tasks/..." className="h-8 text-[11px] bg-card border-border/30" />
                  </div>
                ))}
                <button onClick={() => setTask(p => ({ ...p, internal_links: [...(p.internal_links || []), { name: '', path: '' }] }))} className="text-[10px] text-gold hover:underline">+ Add link</button>
              </div>
              <div className="space-y-1"><Label className="text-[11px] text-foreground/60">Google Drive Link</Label><Input value={task.drive_link || ''} onChange={e => setTask(p => ({ ...p, drive_link: e.target.value }))} placeholder="https://drive.google.com/..." className="h-9 text-[12px] bg-card border-border/30" /></div>
            </div>
          )}
        </div>
        </ErrorBoundary>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-gold/10 hover:bg-gold/20 text-gold border-0" onClick={submit} disabled={submitting} data-testid={`${testIdPrefix}-submit`}>
            {submitting
              ? (activatePrefill ? 'Activating…' : 'Creating…')
              : (activatePrefill ? 'Activate as Task' : 'Create Task')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
